import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, Phone, MessageCircle, FileText, Send, Package, X } from "lucide-react";
import { BRAND, CALL_SALES } from "../lib/constants";
import { absoluteUrl } from "../lib/adminApi";
import { Input } from "../components/ui/input";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildEnquireMsg = (p) =>
  `Hi, I'm interested in ${p.name} (Category: ${p.category}). Please share the price and details.`;

// Share PDF to any contact via WhatsApp contact picker
const buildShareBrochureMsg = (p, pdfAbsUrl) => {
  const lines = [
    `*${p.name}*`,
    p.subcategory ? `${p.category} · ${p.subcategory}` : p.category,
    "",
    pdfAbsUrl ? `Brochure: ${pdfAbsUrl}` : "",
    `Hayat Security & Surveillance · +91 ${BRAND.sales}`,
  ].filter(Boolean).join("\n");
  return lines;
};

const enquireWaUrl = (msg) =>
  `https://wa.me/91${BRAND.sales}?text=${encodeURIComponent(msg)}`;

// Opens WhatsApp contact picker (no number) so user can forward the brochure
const shareWaUrl = (msg) =>
  `https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`;

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/products/categories`),
    ])
      .then(([p, c]) => {
        setProducts(p.data.products || []);
        setCategories(c.data.categories || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (activeCat !== "All") list = list.filter((p) => p.category === activeCat);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(q) ||
          (p.description || "").toLowerCase().includes(q) ||
          (p.category || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [products, activeCat, query]);

  const tabs = ["All", ...categories.filter((c) => products.some((p) => p.category === c))];

  return (
    <div data-testid="products-page" className="bg-white">
      <Seo
        title="CCTV Cameras, Biometrics & Networking Products in Nagpur"
        description="Browse the cameras, biometrics, networking gear and security equipment Hayat Security installs across Nagpur. Trusted brands, expert installation, brochures and pricing on call/WhatsApp."
        canonicalPath="/products"
      />
      <section className="py-8 md:py-10 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            Products
          </h1>
          <div className="relative md:max-w-md w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="products-search"
              placeholder="Search products..."
              className="pl-11 h-11 rounded-full bg-slate-50 border-slate-200"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-100 bg-white sticky top-16 md:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="products-tabs">
            {tabs.length === 1 ? (
              <span className="px-4 py-2 text-sm text-slate-500">No categories yet</span>
            ) : (
              tabs.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCat(cat)}
                  data-testid={`products-tab-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`shrink-0 px-4 h-10 rounded-full text-sm font-semibold transition-colors ${
                    activeCat === cat
                      ? "bg-[#1C6B8A] text-white"
                      : "bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200"
                  }`}
                >
                  {cat}
                </button>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="py-20 text-center text-slate-400 text-sm">Loading products...</div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center" data-testid="products-empty">
              <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="text-slate-700 font-display font-semibold">No products found</div>
              <p className="text-slate-500 text-sm mt-1">Try a different search or category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="products-grid">
              {filtered.map((p, idx) => (
                <ProductCard key={p.id} product={p} idx={idx} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-[#1C6B8A] to-[#0f4a63] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Need help choosing the right product?
          </h2>
          <p className="text-white/80 mt-3 max-w-xl mx-auto">
            Talk to our team — we&apos;ll recommend the right cameras, biometrics or networking gear for your space and budget.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-7">
            <a
              href={CALL_SALES}
              data-testid="products-cta-call"
              className="h-12 px-6 rounded-full bg-white text-[#1C6B8A] font-semibold inline-flex items-center gap-2 hover:bg-slate-100 transition-colors"
            >
              <Phone className="w-4 h-4" /> Call sales
            </a>
            <a
              href={enquireWaUrl("Hi, I'd like a recommendation for the right product for my place.")}
              target="_blank"
              rel="noreferrer"
              data-testid="products-cta-whatsapp"
              className="h-12 px-6 rounded-full bg-[#25D366] hover:bg-[#1eb355] text-white font-semibold inline-flex items-center gap-2 transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function ProductCard({ product, idx }) {
  const stop = (e) => e.stopPropagation();
  const pdfAbs = product.pdf_url ? absoluteUrl(product.pdf_url) : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (idx % 8) * 0.04 }}
      data-testid={`product-card-${product.id}`}
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden card-soft flex flex-col hover:-translate-y-0.5 transition-all"
    >
      <Link
        to={`/products/${product.id}`}
        data-testid={`product-link-${product.id}`}
        className="block"
      >
        <div className="relative aspect-[4/3] bg-white overflow-hidden border-b border-slate-100">
          {product.image_url ? (
            <img
              src={absoluteUrl(product.image_url)}
              alt={product.name}
              className="w-full h-full object-contain p-4"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300">
              <Package className="w-10 h-10" />
            </div>
          )}
          <span className="absolute top-3 left-3 bg-[#E8F4F8] text-[#1C6B8A] text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border border-[#1C6B8A]/15">
            {product.category}
          </span>
          {product.images?.length > 1 && (
            <span className="absolute top-3 right-3 bg-slate-900/80 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
              +{product.images.length - 1} photos
            </span>
          )}
        </div>
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <Link to={`/products/${product.id}`} className="block">
          <h3 className="font-display font-semibold text-slate-900 leading-snug line-clamp-2 hover:text-[#1C6B8A] transition-colors">
            {product.name}
          </h3>
          {product.description && (
            <p className="text-sm text-slate-600 mt-2 leading-relaxed line-clamp-3">
              {product.description}
            </p>
          )}
        </Link>

        <div className="mt-4 grid grid-cols-2 gap-2" onClick={stop}>
          <a
            href={CALL_SALES}
            data-testid={`product-call-${product.id}`}
            className="h-10 rounded-full brand-btn text-xs font-semibold inline-flex items-center justify-center gap-1.5"
          >
            <Phone className="w-3.5 h-3.5" /> Call
          </a>
          <a
            href={enquireWaUrl(buildEnquireMsg(product))}
            target="_blank"
            rel="noreferrer"
            data-testid={`product-whatsapp-${product.id}`}
            className="h-10 rounded-full bg-[#25D366] hover:bg-[#1eb355] text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
          >
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </a>
          {product.pdf_url ? (
            <a
              href={shareWaUrl(buildShareBrochureMsg(product, pdfAbs))}
              target="_blank"
              rel="noreferrer"
              data-testid={`product-send-brochure-${product.id}`}
              className="col-span-2 h-10 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
            >
              <Send className="w-3.5 h-3.5" /> Send brochure on WhatsApp
            </a>
          ) : (
            <a
              href={enquireWaUrl(`Hi, please send me the brochure for ${product.name} (Category: ${product.category}).`)}
              target="_blank"
              rel="noreferrer"
              data-testid={`product-ask-brochure-${product.id}`}
              className="col-span-2 h-10 rounded-full border border-[#1C6B8A] text-[#1C6B8A] hover:bg-[#E8F4F8] text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
            >
              <Send className="w-3.5 h-3.5" /> Ask brochure on WhatsApp
            </a>
          )}
          {product.pdf_url && (
            <a
              href={absoluteUrl(product.pdf_url)}
              target="_blank"
              rel="noreferrer"
              data-testid={`product-pdf-${product.id}`}
              className="col-span-2 h-10 rounded-full border border-slate-200 hover:border-slate-400 text-slate-700 text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
            >
              <FileText className="w-3.5 h-3.5" /> Download brochure (PDF)
            </a>
          )}
          <Link
            to={`/products/${product.id}`}
            data-testid={`product-details-${product.id}`}
            className="col-span-2 h-10 rounded-full border border-slate-200 hover:border-[#1C6B8A] hover:text-[#1C6B8A] text-slate-600 text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
          >
            View full details
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
